import React, { useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCheck,
  FiInbox,
  FiPlus,
  FiSearch,
  FiX,
} from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/fr';
import { useNavigate } from 'react-router-dom';

import {
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  StatusPill,
  Textarea,
  Toolbar,
  cn,
  toast,
} from '../components/ui';
import {
  approveClientRequest,
  getAllClientRequests,
  rejectClientRequest,
} from '../servicesV2/partnerService';
import { downloadFile } from '../servicesV2/entrepriseService';

const COLOR_MAP = {
  Sky: '#0284c7',
  Gold: '#B79043',
  Emerald: '#059669',
  Indigo: '#4f46e5',
  Rose: '#e11d48',
  Orange: '#ea580c',
  Red: '#E33724',
};

/* -------------------------------------------------------------------------- */
/*  Drawer                                                                    */
/* -------------------------------------------------------------------------- */

function RequestDrawer({ request, isOpen, close, onAction }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    setRejectMode(false);
    setRejectReason('');
    setRejectError('');
    // TODO: re-enable once /public/download is fixed for the "master" pseudo-tenant.
    // Currently returns 401 ("Invalid tenant") which trips the global logout interceptor.
    // if (request?.logo) {
    //   downloadFile(request.logo).then(setLogoUrl).catch(() => setLogoUrl(null));
    // } else {
    //   setLogoUrl(null);
    // }
    setLogoUrl(null);
  }, [request?.id, request?.logo]);

  if (!request) return null;

  const partnerName = request.partner_name || request.partner?.company_name || '—';
  const isPending = request.status === 'pending';

  const handleApprove = async () => {
    setApproveLoading(true);
    try {
      await approveClientRequest(request.id);
      toast.success('Demande approuvée', { description: request.company_name });
      onAction?.();
      close();
    } catch (err) {
      console.error(err);
      toast.error('Échec', { description: err?.message });
    } finally {
      setApproveLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setRejectError(t('GLOBAL.REQUIRED_ERROR', { defaultValue: 'Champ requis' }));
      return;
    }
    setRejectLoading(true);
    try {
      await rejectClientRequest(request.id, rejectReason);
      toast.success('Demande rejetée', { description: request.company_name });
      onAction?.();
      close();
    } catch (err) {
      console.error(err);
      toast.error('Échec', { description: err?.message });
    } finally {
      setRejectLoading(false);
    }
  };

  const modules = Array.isArray(request.requested_modules)
    ? request.requested_modules
    : request.requested_modules
      ? [request.requested_modules]
      : [];

  return (
    <Drawer
      isOpen={isOpen}
      close={close}
      title={request.company_name}
      subtitle={`via ${partnerName}`}
      size="md"
    >
      <Drawer.Body className="space-y-5">
        {/* Status strip */}
        <div className="flex items-center justify-between rounded-lg border border-line bg-surface-muted px-4 py-3">
          <div>
            <p className="text-micro text-fg-subtle">Statut</p>
            <div className="mt-1">
              <StatusPill status={request.status} />
            </div>
          </div>
          <div className="text-right">
            <p className="text-micro text-fg-subtle">Soumise</p>
            <p className="mt-1 text-body-sm text-fg">
              {moment(request.created_at).format('DD MMM YYYY HH:mm')}
            </p>
          </div>
        </div>

        {/* Info grid */}
        <section>
          <h3 className="mb-2 text-micro text-fg-subtle">Détails</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-line bg-surface-muted p-4">
            <Kv label="Partenaire" value={partnerName} />
            <Kv label="Client" value={request.company_name} />
            <Kv label="Key" value={request.key} mono />
            <Kv label="Email" value={request.contact_email} />
            <Kv label="Téléphone" value={request.contact_phone} />
          </dl>
        </section>

        {/* Modules */}
        {modules.length > 0 && (
          <section>
            <h3 className="mb-2 text-micro text-fg-subtle">Modules demandés</h3>
            <div className="flex flex-wrap gap-1.5">
              {modules.map((m) => (
                <Badge key={m} variant="brand">
                  {m}
                </Badge>
              ))}
            </div>
          </section>
        )}

        {/* Branding */}
        {(request.color || logoUrl) && (
          <section>
            <h3 className="mb-2 text-micro text-fg-subtle">Identité visuelle</h3>
            <div className="flex items-center gap-4 rounded-lg border border-line bg-surface-muted p-4">
              {logoUrl && (
                <div className="flex h-12 w-12 items-center justify-center rounded-md border border-line bg-surface">
                  <img src={logoUrl} alt="Logo" className="max-h-full max-w-full object-contain" />
                </div>
              )}
              {request.color && (
                <div className="flex items-center gap-2">
                  <span
                    className="h-8 w-8 rounded-md border border-line"
                    style={{ backgroundColor: COLOR_MAP[request.color] || request.color }}
                  />
                  <span className="text-body-sm text-fg">{request.color}</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Rejection reason */}
        {request.status === 'rejected' && request.rejection_reason && (
          <section>
            <h3 className="mb-2 text-micro text-fg-subtle">Motif du rejet</h3>
            <div className="rounded-lg border border-danger-100 bg-danger-50 p-3">
              <p className="inline-flex items-start gap-2 text-body-sm text-danger-700">
                <FiAlertCircle size={14} className="mt-0.5 shrink-0" />
                <span>{request.rejection_reason}</span>
              </p>
            </div>
          </section>
        )}

        {/* Inline reject form */}
        {isPending && rejectMode && (
          <section className="rounded-lg border border-danger-100 bg-danger-50/50 p-4">
            <div className="mb-3 flex items-center gap-2">
              <FiAlertCircle size={14} className="text-danger-600" />
              <p className="text-body-sm font-semibold text-danger-700">
                {t('CLIENT_REQUESTS.REJECT_MODAL.TITLE', { defaultValue: 'Rejeter cette demande ?' })}
              </p>
            </div>
            <Field
              label={t('CLIENT_REQUESTS.REJECT_MODAL.REASON_LABEL', { defaultValue: 'Motif' })}
              required
              error={rejectError}
            >
              <Textarea
                value={rejectReason}
                onChange={(e) => {
                  setRejectReason(e.target.value);
                  setRejectError('');
                }}
                invalid={!!rejectError}
                placeholder={t('CLIENT_REQUESTS.REJECT_MODAL.REASON_PLACEHOLDER', {
                  defaultValue: 'Expliquez pourquoi cette demande est rejetée…',
                })}
                rows={3}
              />
            </Field>
            <div className="mt-3 flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setRejectMode(false);
                  setRejectReason('');
                  setRejectError('');
                }}
              >
                {t('GLOBAL.CANCEL', { defaultValue: 'Annuler' })}
              </Button>
              <Button
                variant="danger"
                size="sm"
                iconLeft={<FiX size={14} />}
                loading={rejectLoading}
                onClick={handleReject}
              >
                {t('CLIENT_REQUESTS.ACTIONS.CONFIRM_REJECT', { defaultValue: 'Confirmer le rejet' })}
              </Button>
            </div>
          </section>
        )}
      </Drawer.Body>

      {isPending && !rejectMode && (
        <Drawer.Footer>
          <Button
            variant="danger-ghost"
            iconLeft={<FiX size={14} />}
            onClick={() => setRejectMode(true)}
          >
            {t('CLIENT_REQUESTS.ACTIONS.REJECT', { defaultValue: 'Rejeter' })}
          </Button>
          <Button
            variant="secondary"
            iconLeft={<FiPlus size={14} />}
            onClick={() => {
              close();
              navigate(`/client-requests/${request.id}/create-client`);
            }}
          >
            {t('CLIENT_REQUESTS.ACTIONS.CREATE_CLIENT', { defaultValue: 'Créer le client' })}
          </Button>
          <Button
            variant="primary"
            iconLeft={<FiCheck size={14} />}
            loading={approveLoading}
            onClick={handleApprove}
          >
            {t('CLIENT_REQUESTS.ACTIONS.APPROVE', { defaultValue: 'Approuver' })}
          </Button>
        </Drawer.Footer>
      )}
    </Drawer>
  );
}

function Kv({ label, value, mono }) {
  return (
    <div className="min-w-0">
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 truncate text-body-sm text-fg',
          mono && 'font-mono text-caption',
        )}
      >
        {value || '—'}
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

function ClientRequests() {
  const { t } = useTranslation();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await getAllClientRequests();
      setRequests(Array.isArray(res) ? res : res?.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Chargement impossible', { description: err?.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return requests.filter((r) => {
      const partnerName = r.partner_name || r.partner?.company_name || '';
      const matchQ =
        !q ||
        [r.company_name, partnerName, r.key, r.contact_email]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      const matchS = !statusFilter || r.status === statusFilter;
      return matchQ && matchS;
    });
  }, [requests, query, statusFilter]);

  const isFiltered = query.length > 0 || statusFilter.length > 0;
  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  const openDrawer = (row) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const columns = [
    {
      id: 'partner',
      header: t('CLIENT_REQUESTS.TABLE.PARTNER', { defaultValue: 'Partenaire' }),
      accessor: (row) => row.partner_name || row.partner?.company_name || '',
      sortable: true,
      cell: (row) => (
        <span className="text-fg-muted">
          {row.partner_name || row.partner?.company_name || '—'}
        </span>
      ),
    },
    {
      id: 'company',
      header: t('CLIENT_REQUESTS.TABLE.COMPANY_NAME', { defaultValue: 'Client' }),
      accessor: 'company_name',
      sortable: true,
      cell: (row) => (
        <span className="truncate font-medium text-fg">{row.company_name}</span>
      ),
    },
    {
      id: 'key',
      header: t('CLIENTS.TABLE.KEY', { defaultValue: 'Key' }),
      accessor: 'key',
      mono: true,
    },
    {
      id: 'modules',
      header: t('CLIENT_REQUESTS.TABLE.MODULES', { defaultValue: 'Modules' }),
      cell: (row) => {
        const mods = Array.isArray(row.requested_modules)
          ? row.requested_modules
          : row.requested_modules
            ? [row.requested_modules]
            : [];
        if (mods.length === 0) return <span className="text-fg-subtle">—</span>;
        return (
          <div className="flex items-center gap-1">
            {mods.slice(0, 2).map((m) => (
              <Badge key={m} variant="brand" size="sm">
                {m}
              </Badge>
            ))}
            {mods.length > 2 && (
              <Badge variant="neutral" size="sm">
                +{mods.length - 2}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: 'submitted',
      header: t('CLIENT_REQUESTS.TABLE.SUBMITTED_AT', { defaultValue: 'Soumise' }),
      accessor: 'created_at',
      sortable: true,
      cell: (row) => (
        <span className="text-fg-muted">{moment(row.created_at).fromNow()}</span>
      ),
    },
    {
      id: 'status',
      header: t('CLIENT_REQUESTS.TABLE.STATUS', { defaultValue: 'Statut' }),
      accessor: 'status',
      sortable: true,
      cell: (row) => <StatusPill status={row.status} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('CLIENT_REQUESTS.TITLE', { defaultValue: 'Requests' })}
        count={requests.length}
        description="Demandes de provisionnement soumises par vos partenaires."
        meta={
          pendingCount > 0 && (
            <Badge variant="warning" dot>
              {pendingCount} en attente
            </Badge>
          )
        }
      />

      <Toolbar
        left={
          <>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Rechercher par partenaire, client, key…"
              leading={<FiSearch size={13} />}
              containerClassName="max-w-sm w-full"
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="max-w-[180px]"
            >
              <option value="">{t('CLIENT_REQUESTS.FILTERS.ALL', { defaultValue: 'Tous les statuts' })}</option>
              <option value="pending">{t('CLIENT_REQUESTS.FILTERS.PENDING', { defaultValue: 'En attente' })}</option>
              <option value="approved">{t('CLIENT_REQUESTS.FILTERS.APPROVED', { defaultValue: 'Approuvé' })}</option>
              <option value="provisioning">Provisionnement</option>
              <option value="rejected">{t('CLIENT_REQUESTS.FILTERS.REJECTED', { defaultValue: 'Rejeté' })}</option>
            </Select>
            {isFiltered && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setQuery('');
                  setStatusFilter('');
                }}
              >
                Réinitialiser
              </Button>
            )}
          </>
        }
      />

      <DataTable
        columns={columns}
        data={filtered}
        rowKey="id"
        onRowClick={openDrawer}
        loading={loading}
        isFiltered={isFiltered}
        initialSort={{ columnId: 'submitted', direction: 'desc' }}
        pageSize={25}
        empty={
          <EmptyState
            icon={<FiInbox size={22} />}
            title="Tout est à jour"
            description="Aucune demande de provisionnement n'est en attente."
          />
        }
        emptyFiltered={
          <EmptyState
            icon={<FiSearch size={22} />}
            title="Aucun résultat"
            description="Essayez d'assouplir les filtres."
            action={
              <Button
                variant="secondary"
                onClick={() => {
                  setQuery('');
                  setStatusFilter('');
                }}
              >
                Réinitialiser
              </Button>
            }
          />
        }
      />

      <RequestDrawer
        request={selected}
        isOpen={drawerOpen}
        close={() => setDrawerOpen(false)}
        onAction={() => {
          fetchRequests();
        }}
      />
    </>
  );
}

export default ClientRequests;
